import React from 'react';
import { Box, CircularProgress, Typography, styled } from '@mui/material';
import { DateRangePicker, DateRangePickerProps } from 'components/DateRangePicker/DateRangePicker';
import { WeightWidgetCard } from 'components/WeightWidgetCard/WeightWidgetCard';
import {
  WidgetReadingList,
  ReadingRow,
} from 'components/ProgressTab/WidgetDetail/WidgetReadingList';

const Root = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  width: '100%',
}));

const Section = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 'none',
  [theme.breakpoints.up('md')]: {
    maxWidth: 743,
  },
}));

const FixedTop = styled(Section)(({ theme }) => ({
  paddingTop: theme.spacing(4.25),
}));

const DetailChartCard = styled(WeightWidgetCard)({
  '&&': {
    width: '100%',
    maxWidth: 'none',
    padding: 0,
  },
  '& .MuiCard-root': {
    boxShadow: 'none',
    borderRadius: 0,
  },
  '& .MuiCardContent-root': {
    '&:last-child': {
      paddingBottom: 0,
    },
  },
});

const CenterState = styled(Section)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: theme.spacing(4.25),
}));

export interface WeightDetailsViewProps {
  dateRange: DateRangePickerProps;
  dates: string[];
  weights: (number | null)[];
  latestValue: number | string;
  unit?: string;
  caption?: string;
  readings: ReadingRow[];
  isLoading?: boolean;
  isError?: boolean;
  onWidgetClick: () => void;
  onDownload?: () => void;
  isDownloading?: boolean;
  className?: string;
}

export const WeightDetailsView: React.FC<WeightDetailsViewProps> = ({
  dateRange,
  dates,
  weights,
  latestValue,
  unit,
  caption,
  readings,
  isLoading = false,
  isError = false,
  onWidgetClick,
  onDownload,
  isDownloading = false,
  className,
}) => {
  if (isLoading) {
    return (
      <Root className={className}>
        <CenterState>
          <CircularProgress aria-label="Loading weight data" />
        </CenterState>
      </Root>
    );
  }

  if (isError) {
    return (
      <Root className={className}>
        <CenterState>
          <Typography color="error">Unable to load weight data.</Typography>
        </CenterState>
      </Root>
    );
  }

  return (
    <Root className={className}>
      <FixedTop>
        <DateRangePicker {...dateRange} />
        <DetailChartCard
          onClick={onWidgetClick}
          dates={dates}
          weights={weights}
          value={latestValue}
          unit={unit}
          caption={caption}
          showLastReading={false}
          showHeader={false}
        />
      </FixedTop>
      <Section>
        <WidgetReadingList
          readings={readings}
          onDownload={onDownload}
          isDownloading={isDownloading}
        />
      </Section>
    </Root>
  );
};
